import { FC, MouseEvent, useMemo } from "react";
import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import { useAppStore } from "@/store";
import { MDSIcon, MDSIconColor, MDSIconKind } from "@/mds";
import { FloatingNotePreview, ItemPreviewHoveringState } from "@/components/item-preview";

interface CitationChipProps {
  noteId: string;
  label?: string;
  variant?: "inline" | "icon" | "pill";
}

export const CitationChip: FC<CitationChipProps> = observer(function CitationChip({
  noteId,
  label,
  variant = "pill",
}) {
  const { store } = useAppStore();
  const hoverState = useMemo(() => new ItemPreviewHoveringState(), []);

  const noteObservable = store.notes.getNoteObservableById({ noteId });
  const title =
    noteObservable?.suggestionData?.suggested_primary_label ||
    noteObservable?.title ||
    "Source note";
  const inlineLabel = label ?? "source";
  const pillLabel = label ?? title;

  const onClick = (e: MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    hoverState.deactivateImmediately();
    store.navigation.goToNote({ noteId });
  };

  const handleMouseEnter = (event: MouseEvent<HTMLElement>) => {
    hoverState.setHoveredRect(event.currentTarget.getBoundingClientRect());
    hoverState.handleMouseEnter();
  };

  const handleMouseLeave = () => {
    hoverState.setHoveredRect(null);
    hoverState.handleMouseLeave();
  };

  const chip =
    variant === "inline" ? (
      <InlineChip
        onClick={onClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        aria-label={`Source: ${title}`}
      >
        [{inlineLabel}]
      </InlineChip>
    ) : variant === "icon" ? (
      <IconChip
        onClick={onClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        aria-label={`Source: ${title}`}
      >
        <MDSIcon kind={MDSIconKind.Document} size={10} boxSize={14} color={MDSIconColor.Inherit} />
      </IconChip>
    ) : (
      <PillChip
        onClick={onClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        aria-label={`Source: ${title}`}
        title={title}
      >
        <MDSIcon kind={MDSIconKind.Document} size={10} boxSize={12} color={MDSIconColor.Inherit} />
        <PillLabel>{pillLabel}</PillLabel>
      </PillChip>
    );

  return (
    <ChipWrapper>
      {chip}
      {noteObservable && <FloatingNotePreview state={hoverState} noteObservable={noteObservable} />}
    </ChipWrapper>
  );
});

const ChipWrapper = styled.div`
  position: relative;
  display: inline-flex;
  flex: 0 1 auto;
  max-width: 100%;
  min-width: 0;
  vertical-align: baseline;
`;

const InlineChip = styled.button`
  display: inline;
  padding: 0 2px;
  margin: 0 2px;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.text.tertiary};
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  vertical-align: super;
  line-height: 1;

  &:hover {
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

const IconChip = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  padding: 0;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.text.tertiary};
  cursor: pointer;
  font-family: inherit;
  border-radius: 3px;
  flex-shrink: 0;
  &:hover {
    background: ${({ theme }) => theme.colors.background.button.onChrome.hover};
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

const PillChip = styled.button`
  display: inline-flex;
  align-items: center;
  flex: 0 1 auto;
  gap: 4px;
  max-width: min(184px, 100%);
  min-width: 0;
  height: 22px;
  padding: 0 7px;
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.background.button.onChrome.default};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 12px;
  font-family: inherit;
  line-height: 1;
  cursor: pointer;
  vertical-align: middle;

  &:hover {
    background: ${({ theme }) => theme.colors.background.button.onChrome.hover};
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

const PillLabel = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
